import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Opens the header shopify-account sheet when the component is on the page.
 * @returns {Promise<boolean>} Whether the account sheet was opened
 */
export async function openShopifyAccount() {
  const account = document.querySelector('shopify-account');
  if (!account) return false;

  await customElements.whenDefined('shopify-account');

  const shadowRoot = account.shadowRoot;
  if (shadowRoot) {
    const button =
      shadowRoot.querySelector('button[popovertarget], button[aria-haspopup]') ??
      shadowRoot.querySelector('button');
    if (button instanceof HTMLElement) {
      button.click();
      return true;
    }
  }

  if (account instanceof HTMLElement) {
    account.click();
    return true;
  }

  return false;
}

/**
 * @param {MouseEvent} event
 */
async function handleCartSignInClick(event) {
  const link = event.target instanceof Element ? event.target.closest('.cart-items__sign-in a') : null;
  if (!link || !document.querySelector('shopify-account')) return;

  event.preventDefault();

  const opened = await openShopifyAccount();
  if (!opened && link instanceof HTMLAnchorElement && link.href) {
    window.location.assign(link.href);
  }
}

document.addEventListener('click', handleCartSignInClick);

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
